import React, { PureComponent } from 'react';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Button,
  Cascader,
  DatePicker,
  InputNumber,
  Popover,
  Select,
} from 'antd';

import { timezones, weekOptions } from '../options';
import { BaseInputProps } from '../types';
import { isNullLike } from '../utils';

import { Text } from './Text';
import dayjs from 'dayjs';

export interface EventListInfo {
  durationHour: number;
  eventName: string;
  eventType: string;
  extraDay: number;
  startTime: string | string[];
  timeZone: string;
  toCalibrate: boolean;
}

export interface EventListProps extends BaseInputProps {
  value?: EventListInfo[];

  onChange?: (val: EventListInfo[]) => void;

  options?: string[];
}

export class EventList extends PureComponent<EventListProps> {
  value: EventListInfo[] = [];

  isEmptyItem = (item: EventListInfo) => {
    return (
      !item.eventName?.trim() &&
      isNullLike(item.eventType) &&
      !item.timeZone?.trim() &&
      !(Array.isArray(item.startTime) ? !!item.startTime.length : item.startTime?.trim()) &&
      !item.durationHour &&
      !item.extraDay
    );
  };

  onChange = (i: number, val: any, key?: keyof EventListInfo) => {
    const value = [...this.value];
    if (!value[i])
      value[i] = {
        durationHour: 0,
        eventName: '',
        eventType: 'Non-weekend',
        extraDay: 0,
        startTime: '',
        timeZone: (window as any).timeZone,
        toCalibrate: true,
      };
    if (key) value[i][key] = val as never;
    else value[i] = { ...value[i], ...val };
    this.props.onChange?.(
      value.length === 1 && this.isEmptyItem(value[0]) ? [] : value,
    );
  };

  onAdd = (i: number) => {
    const value = [...this.value];
    value.splice(i + 1, 0, {
      durationHour: 0,
      eventName: '',
      eventType: 'Non-weekend',
      extraDay: 0,
      startTime: '',
      timeZone: (window as any).timeZone,
      toCalibrate: true,
    });
    this.props.onChange?.(value);
    this.props.onBlur?.();
  };

  onDel = (i: number) => {
    const value = [...this.value];
    value.splice(i, 1);
    this.props.onChange?.(
      value.length === 1 && this.isEmptyItem(value[0]) ? [] : value,
    );
    this.props.onBlur?.();
  };

  render() {
    this.value = this.props.value || [];
    // if (!this.value.length)
    //   this.value.push({
    //     durationHour: 0,
    //     eventName: '',
    //     eventType: 'Non-weekend',
    //     extraDay: 0,
    //     startTime: '',
    //     timeZone: (window as any).timeZone,
    //     toCalibrate: true,
    //   });
    return (
      <div
        className={this.props.className}
        style={this.props.style}
      >
        {this.props.readonly ? (
          <Popover
            trigger="click"
            placement="topRight"
            content={
              <>
                {this.value?.length
                  ? this.value.map((it, i) => (
                      <span key={i}>
                        {it.eventName || '-'}
                        <span className='text-primary'>/</span>
                        {it.eventType || '-'}
                        <span className='text-primary'>/</span>
                        {it.timeZone || '-'}
                        <span className='text-primary'>/</span>
                        {it.startTime || '-'}
                        <span className='text-primary'>/</span>
                        {it.durationHour || '-'}
                        <span className='text-primary'>/</span>
                        {it.extraDay || '-'}
                        <span className='text-primary'>/</span>
                        {it.toCalibrate
                          ? 'True'
                          : it.toCalibrate === false
                            ? 'False'
                            : '-'}
                      </span>
                    ))
                  : '-'}
              </>
            }
          >
            <Button className='text-primary!' type="text">
              Click to show more
            </Button>
          </Popover>
        ) : (
          <div className="grid grid-cols-10 gap-4 max-w-10xl w-360">
            {/* 表头 */}
            <div className="font-medium text-gray-700">Event</div>
            <div className="font-medium text-gray-700">Event Type</div>
            <div className="font-medium text-gray-700"></div>
            <div className="font-medium text-gray-700">Time Zone</div>
            <div className="font-medium text-gray-700"></div>
            <div className="font-medium text-gray-700">Start Time</div>
            <div className="font-medium text-gray-700">DurationHour</div>
            <div className="font-medium text-gray-700">ExtraDay</div>
            <div className="font-medium text-gray-700">ToCalibrate</div>
            <div className="font-medium text-gray-700">
              {
                this.value?.length <= 0 && <>
                  <button
                    onClick={() => this.onAdd(0)}
                    className="p-1 rounded hover:bg-blue-100 transition-colors"
                  >
                    <PlusOutlined/>
                  </button>
                </>
              }
            </div>
            
            {/* 数据行 */}
            {this.value.map((it, i) => (
              <React.Fragment key={i}>
                {/* Event Name */}
                <div className="col-span-1">
                  <Text
                    value={it.eventName}
                    onChange={val => this.onChange(i, val, 'eventName')}
                  />
                </div>
                
                {/* Event Type */}
                <div className="col-span-2">
                  <Select
                    className="w-full"
                    value={[it.eventType]}
                    onChange={val => {
                      this.onChange(i, val, 'eventType');
                      this.onChange(i, undefined, 'startTime');
                    }}
                    options={['Weekend', 'Non-weekend'].map(it => ({
                      value: it,
                      label: it,
                    }))}
                  />
                </div>
                
                {/* Time Zone */}
                <div className="col-span-2">
                  <Select
                    className="w-full"
                    value={it.timeZone}
                    onChange={val => this.onChange(i, val, 'timeZone')}
                    options={timezones}
                    optionFilterProp="children"
                    showSearch
                    filterOption={(input, option) => {
                      return (option?.label.toLowerCase() ?? '').includes(
                        input.toLowerCase(),
                      );
                    }}
                  />
                </div>
                
                {/* Start Time */}
                <div className="col-span-1">
                  {it.eventType === 'Weekend' ? (
                    <Cascader
                      options={weekOptions}
                      onChange={val => this.onChange(i, val[1], 'startTime')}
                      placeholder="Please select"
                      className="w-full"
                      value={it.startTime as string[]}
                    />
                  ) : (
                    <DatePicker
                      className="w-full"
                      format="YYYY-MM-DD HH:mm:ss"
                      showTime
                      value={dayjs(it.startTime as string)}
                      onChange={val => this.onChange(i, val, 'startTime')}
                    />
                  )}
                </div>
                
                {/* Duration Hour */}
                <div className="col-span-1">
                  <InputNumber
                    value={it.durationHour}
                    onChange={val => this.onChange(i, val, 'durationHour')}
                    className="w-full"
                    step={0.1}
                    precision={1}
                  />
                </div>
                
                {/* Extra Day */}
                <div className="col-span-1">
                  <InputNumber
                    value={it.extraDay}
                    onChange={val => this.onChange(i, val, 'extraDay')}
                    className="w-full"
                    step={0.001}
                    precision={3}
                  />
                </div>
                
                {/* To Calibrate */}
                <div className="col-span-1">
                  <Select
                    className="w-full"
                    value={it.toCalibrate}
                    onChange={val => this.onChange(i, val, 'toCalibrate')}
                    options={[
                      { value: true, label: 'True' },
                      { value: false, label: 'False' },
                    ]}
                  />
                </div>
                
                {/* 操作按钮 */}
                <div className="col-span-1 flex space-x-2">
                  <button
                    onClick={() => this.onAdd(i)}
                    className="p-1 rounded hover:bg-blue-100 transition-colors"
                  >
                    <PlusOutlined className="text-blue-600" />
                  </button>
                  <button
                    onClick={() => this.onDel(i)}
                    className="p-1 rounded hover:bg-red-100 transition-colors"
                  >
                    <DeleteOutlined className="text-red-600" />
                  </button>
                </div>
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
    );
  }
}
